import _filter from 'lodash/filter';
import _map from 'lodash/map';
import {DateTime} from 'luxon';
import apiV2Service from '../api/api-v2.service';
import ObjectStatus from '../enums/static/ObjectStatus';
import {ProductType} from '../products/products.enums';
import {UTCTimezoneObject} from '../timezones/timezones.consts';
import {TimezoneObject} from '../timezones/timezones.types';
import getTimeZoneStringForDateTime from '../timezones/utils/getTimezoneStringForDateTime';
import {
  toClientDataSource,
  toClientDataSourceStatistics,
  toDataSourceStatisticsPixelData,
} from './dataSources.dataConverters';
import {
  AddDataSourceParams,
  AdditionalConfigurationParams,
  DataSource,
  DataSourceMapping,
  DatasourceRuleOption,
  DataSourceStatistics,
  PixelData,
  RemoteDataSource,
  RemoteDataSourceStatistics,
  TimeUnitForPixel,
} from './dataSources.types';
import isUserProfilingRelatedDataSourceType from './utils/isUserProfilingRelatedDataSourceType';
import {get} from 'lodash';

const basePath = 'DataSources';

async function getDataSources(): Promise<DataSource[]> {
  const {data: dataSources} = await apiV2Service.executeGetRequest<RemoteDataSource[]>(
    `${basePath}`,
  );

  return _map(dataSources, toClientDataSource);
}

async function getDataSource(id: number): Promise<DataSource> {
  const {data: dataSource} = await apiV2Service.executeGetRequest<RemoteDataSource>(
    `${basePath}/${id}`,
  );

  return toClientDataSource(dataSource);
}

async function deleteDataSource(id: number): Promise<void> {
  const {data} = await apiV2Service.executeDeleteRequest<void>(`${basePath}/${id}`);

  return data;
}

async function add({name, type, partnerId, productType}: AddDataSourceParams): Promise<number> {
  const {data} = await apiV2Service.executePostRequest<number>(
    `${basePath}/Add`,
    {},
    {
      name,
      type,
      ...(!!partnerId && {partnerId}),
      ...(productType != null && {productType}),
    },
  );

  return data;
}

async function update({
  dataSourceId,
  name,
  numberLocalization,
  dateFormat,
  subjectIdentifiers,
}: {
  dataSourceId: number;
  name?: string;
  numberLocalization?: string;
  dateFormat?: string;
  subjectIdentifiers?: string[];
}) {
  return apiV2Service.executePatchRequest(
    `${basePath}/${dataSourceId}`,
    {},
    {
      ...(name && {name}),
      ...(numberLocalization && {numberLocalization}),
      ...(dateFormat && {dateFormat}),
      ...(subjectIdentifiers && {subjectIdentifiers}),
    },
  );
}

async function getConversionTypes(): Promise<string[]> {
  const {data: conversionSubTypes} = await apiV2Service.executeGetRequest<string[]>(
    `${basePath}/GetConversionTypes`,
  );

  return conversionSubTypes;
}

async function getDataSourceDetails({
  fromDate,
  toDate,
  pixelId,
  productType,
  selectedTimezone,
}: {
  fromDate: DateTime;
  toDate: DateTime;
  pixelId?: number;
  productType?: ProductType;
  selectedTimezone: TimezoneObject;
}): Promise<DataSourceStatistics[]> {
  const timeUnit = fromDate.equals(toDate) ? TimeUnitForPixel.Hourly : TimeUnitForPixel.Daily;

  const timezone = getTimeZoneStringForDateTime(selectedTimezone);
  const params = {
    parameters: {
      fromDate: fromDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      toDate: toDate.setZone(timezone, {keepLocalTime: true}).toISO(),
      ...(productType && {productType}),
    },
    ...(pixelId && {pixelId}),
    timeUnit,
  };

  const {data: pixels} = await apiV2Service.executePostRequest<PixelData[]>(
    `${basePath}/GetDataSourceDetails`,
    {},
    params,
  );

  return _map(pixels, toDataSourceStatisticsPixelData);
}

async function getPixelDocumentationXlsx({
  pixelId,
  vertical,
  pixelType,
}: {
  pixelId: number;
  vertical: string;
  pixelType?: string;
}): Promise<string> {
  const {data: url} = await apiV2Service.executePostRequest<string>(
    `${basePath}/GetPixelDocumentation`,
    {},
    {
      pixelId,
      vertical,
      ...(pixelType ? {pixelType} : {}),
    },
  );

  return url;
}

async function getMappingOptions(): Promise<DatasourceRuleOption[]> {
  const {data} = await apiV2Service.executeGetRequest<DatasourceRuleOption[]>(
    `${basePath}/fieldsmapping/options`,
  );

  return data;
}

async function getFieldsMapping(datasourceId: number): Promise<DataSourceMapping[]> {
  const {data} = await apiV2Service.executeGetRequest<DataSourceMapping[]>(
    `${basePath}/${datasourceId}/fieldsmapping`,
  );

  return data;
}

async function updateFieldsMapping(
  datasourceId: number,
  mappings: DataSourceMapping[],
): Promise<[]> {
  const {data} = await apiV2Service.executePostRequest<[]>(
    `${basePath}/${datasourceId}/fieldsmapping`,
    {datasourceId},
    {mappings},
  );

  return data;
}

async function addOfflineDataSource(params: AddDataSourceParams): Promise<number> {
  const {data: dataSourceId} = await apiV2Service.executePutRequest<number>(
    `${basePath}/OfflineEvents`,
    {},
    {...params},
  );

  return dataSourceId;
}

async function addThirdPartyDataSource(params: AddDataSourceParams): Promise<number> {
  const {data: dataSourceId} = await apiV2Service.executePutRequest<number>(
    `${basePath}/ExternalAggregatedData`,
    {},
    {...params},
  );

  return dataSourceId;
}

async function getDataSourcesStatistics(offset: number): Promise<DataSourceStatistics[]> {
  const {data: stats} = await apiV2Service.executeGetRequest<RemoteDataSourceStatistics[]>(
    `${basePath}/statistics/yesterday`,
    {offset},
  );

  return stats.map(toClientDataSourceStatistics);
}

async function getExternalConversionNames(dataSourceId: number): Promise<string[]> {
  const {data} = await apiV2Service.executeGetRequest<string[]>(
    `${basePath}/ExternalConversionNames`,
    {dataSourceId},
  );

  return data;
}

async function executeDataSourceIdentityHealthReportJob(
  datasourceId: number,
  date: DateTime,
): Promise<number> {
  const timezone = getTimeZoneStringForDateTime(UTCTimezoneObject);
  const {data} = await apiV2Service.executePostRequest<number>(
    `${basePath}/RunDataSourceIdentityHealthReportJob`,
    {},
    {datasourceId, date: date.setZone(timezone, {keepLocalTime: true}).toISO()},
  );

  return data;
}

async function executeDataSourceEtypeHealthReportJob(
  datasourceId: number,
  date: DateTime,
): Promise<number> {
  const timezone = getTimeZoneStringForDateTime(UTCTimezoneObject);
  const {data} = await apiV2Service.executePostRequest<number>(
    `${basePath}/RunDataSourceEtypeHealthReportJob`,
    {},
    {datasourceId, date: date.setZone(timezone, {keepLocalTime: true}).toISO()},
  );

  return data;
}

async function doesClientHaveActiveUserProfilingRelatedDataSource(
  clientId: number,
): Promise<boolean> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDataSource[]>(
    `${clientId}/${basePath}`,
    {},
    {includeClientId: false},
  );

  const isUserProfilingRelatedDataSources = _filter(
    data,
    ({type, status}: RemoteDataSource) =>
      isUserProfilingRelatedDataSourceType(type) && status === ObjectStatus.Active,
  );

  return !!isUserProfilingRelatedDataSources.length;
}

async function getAdditionalConfigurationParams(): Promise<AdditionalConfigurationParams> {
  const {data} = await apiV2Service.executeGetRequest<AdditionalConfigurationParams>(
    `${basePath}/GetAdditionalConfigurationParams`,
    {},
  );

  return data;
}

async function activateDataSource(dataSourceId: number): Promise<void> {
  const {data} = await apiV2Service.executePostRequest<void>(
    `${basePath}/${dataSourceId}/Activate`,
  );

  return data;
}

async function disableDataSource(dataSourceId: number): Promise<void> {
  const {data} = await apiV2Service.executePostRequest<void>(`${basePath}/${dataSourceId}/Disable`);

  return data;
}

export default {
  get,
  getDataSources,
  deleteDataSource,
  add,
  update,
  getConversionTypes,
  getPixelDocumentationXlsx,
  getMappingOptions,
  getFieldsMapping,
  updateFieldsMapping,
  addOfflineDataSource,
  addThirdPartyDataSource,
  getDataSourcesStatistics,
  getExternalConversionNames,
  executeDataSourceIdentityHealthReportJob,
  executeDataSourceEtypeHealthReportJob,
  doesClientHaveActiveUserProfilingRelatedDataSource,
  getAdditionalConfigurationParams,
  getDataSourceDetails,
  getDataSource,
  activateDataSource,
  disableDataSource,
};
